/**
 * Firm Success — the per-firm read surface (#676). The card/definition layer
 * that once defined these numbers was wiped under the ruled catalog entry
 * wipe_measurement_presentation_layer (#740, 2026-08-20); the rebuilt
 * measurement surface reads the Contract (engineering/contract/).
 *
 * Sourcing (ruled #676 session 06): every element reads the LIVE book — flows
 * and stocks alike, so no element contradicts the block beside it.
 *
 * Windows are PER CARD (#680), not one window for the surface: lifetime for the
 * censuses, maps and destinations; a rolling 90 days for the two
 * clocks, measured on their own end stamps; the chart's own window for the
 * graphs and the type mix; and one data-limited exclusion on
 * of_unmatched_confirmed, whose predicate reads the absence of a record and
 * sets aside the rows the April migration's batches moved
 * (engineering/contract/provenance.ts migration_2026_04, its signature says
 * why). The registry remains the definitional contract
 * over whichever source is read; the atoms keep accumulating as an archive that
 * nothing on this surface consumes.
 *
 * Conversion elements were REMOVED by the #740 wipe: the shipped rule
 * contradicted the ratified Conversion term (glossary.yaml) and is not
 * reconciled, it is rebuilt from the Contract.
 *
 * Time clocks: TWO, not three (ruled session 05 — Date Responded and
 * decisionDate stamp together in practice, so an acknowledge/decide pair
 * would show the same number twice): send→decision and decision→FSA-return.
 * Medians, with n beside every figure.
 *
 * Case-type buckets split Personal injury into PI leads vs PI transfers by
 * the lead's Signed flag, read straight off the lead. Minor types bucket
 * into "Other types".
 */

import { z } from "zod";

/** Display buckets for case types. PI splits by the lead's Signed flag. */
export const FS_CASE_TYPE_BUCKETS = [
  "Employment law",
  "PI leads",
  "PI transfers",
  "Workers' comp",
  "Med mal",
  "Other types",
] as const;

export const FsCapacitySchema = z.enum(["of", "hf"]);
export type FsCapacity = z.infer<typeof FsCapacitySchema>;

/** One day of the series. byType keys are display buckets. */
export const FsDailyPointSchema = z
  .object({
    date: z.string(),
    total: z.number().int().min(0),
    byType: z.record(z.string(), z.number().int().min(0)),
  })
  .strict();

/** The 90-day daily series, from live stamps.
 *
 *  The window is the full 90 days ending yesterday, and every day in it is
 *  measured from live stamps, which read the whole book (ruled #676 session
 *  06) — so there is no recorded/un-recorded split. */
export const FsDailySeriesSchema = z
  .object({
    /** First day of the frame — today−90d. */
    start: z.string(),
    /** Last drawn day — yesterday; days that haven't happened are never drawn. */
    end: z.string(),
    points: z.array(FsDailyPointSchema),
    /** The firm's busiest single day (axis anchor: top ≈ +30%). */
    busiestDay: z.number().int().min(0),
  })
  .strict();

/** One calendar cell.
 *
 *  Sourcing note (ruled #676 session 06): the five-week calendar reads LIVE
 *  arrival / send stamps, not atoms — the same live read the list rows do.
 *  The no-backfill ruling covers the 90-day graphs; extending it here blanked
 *  ~half the grid, which is what "the borders are absent" was. So every past
 *  day carries a real count (zero included) and only today and future days
 *  are null. */
export const FsCalendarDaySchema = z
  .object({
    date: z.string(),
    /** null ONLY for today (still counting) and days that haven't happened. */
    count: z.number().int().min(0).nullable(),
    /** Today — still filling, drawn distinct from finished days. */
    today: z.boolean(),
  })
  .strict();

export const FsCalendarWeekSchema = z
  .object({
    label: z.string(),
    days: z.array(FsCalendarDaySchema).length(7),
    total: z.number().int().min(0),
  })
  .strict();

export const FsStatusCardSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    count: z.number().int().min(0),
    /** e.g. "incl. 3 pulled" on the HF Declined card. */
    note: z.string().nullable(),
  })
  .strict();

export const FsCaseRowSchema = z
  .object({
    leadName: z.string(),
    caseType: z.string(),
    state: z.string().nullable(),
    /** Arrived (OF cards) or sent-to-firm (HF cards) date, ISO. */
    date: z.string().nullable(),
    /** Exact placement decision (HF cards only; null on OF rows). */
    exactStatus: z.string().nullable(),
  })
  .strict();

export const FsConvCellSchema = z
  .object({
    placed: z.number().int().min(0),
    /** OF: leads resolved. HF: placements decided. */
    denominator: z.number().int().min(0),
  })
  .strict();

export const FsConversionRowSchema = z
  .object({
    bucket: z.string(),
    mine: FsConvCellSchema,
    others: FsConvCellSchema,
  })
  .strict();

/** One 30-day block of a clock row: the median and the n it rests on. */
export const FsClockBlockSchema = z
  .object({
    medianDays: z.number().nullable(),
    n: z.number().int().min(0),
  })
  .strict();

export const FsClockRowSchema = z
  .object({
    /** null = the overall row (total first, then per type — R15). */
    bucket: z.string().nullable(),
    /** The Firm's median over the whole clock window, with its n. */
    mineMedianDays: z.number().nullable(),
    mineN: z.number().int().min(0),
    /** The same samples split into the window's 30-day blocks by end-stamp
     *  day, newest first: the last 30 days, 30–60 days ago, 60–90 days ago.
     *  The Firm read against its own months, inside Chris's three-month
     *  horizon (#680). Until #1267 this row carried every-other-Firm columns
     *  (their median and n), pooled across Case Types and waterfall
     *  positions; Chris's later words limit a cross-Firm comparison to
     *  within waterfall position (Firm-Success#36), which that pool was not. */
    byThirtyDays: z.array(FsClockBlockSchema).length(3),
  })
  .strict();

export const FsMapEntrySchema = z
  .object({
    state: z.string(),
    total: z.number().int().min(0),
    byType: z.record(z.string(), z.number().int().min(0)),
  })
  .strict();

export const FsDestinationRowSchema = z
  .object({
    firmId: z.string(),
    name: z.string(),
    received: z.number().int().min(0),
    placed: z.number().int().min(0),
    declined: z.number().int().min(0),
    awaiting: z.number().int().min(0),
  })
  .strict();

export const FsOfPageSchema = z
  .object({
    daily: FsDailySeriesSchema,
    calendar: z.array(FsCalendarWeekSchema),
    statusCards: z.array(FsStatusCardSchema),
    statusCases: z.record(z.string(), z.array(FsCaseRowSchema)),
    /** Lead-type mix over the graph's own window — the pie (#680: OF-3 is
     *  present tense, and a lifetime pie beside a windowed graph does not
     *  reconcile). */
    mix: z.array(
      z.object({ bucket: z.string(), count: z.number().int().min(0) }).strict(),
    ),
    mixTotal: z.number().int().min(0),
    /** Live: leads at status Unmatched right now. */
    unmatchedNow: z.number().int().min(0),
    /** Live: rejected leads with zero placements — never routed — among the
     *  firm's leads whose placement history is trusted (not a row the April
     *  migration's batches moved; provenance migration_2026_04). */
    neverRouted: z.number().int().min(0),
    /** Live: the denominator of the same segment — the firm's leads with a
     *  trusted placement history. */
    neverRoutedOf: z.number().int().min(0),
    /** Live: the firm's leads the April migration's batches moved — 04-06's
     *  first pass and 04-20 without their placement rows, 04-06's second pass
     *  and the evening waves with them — cannot be classified routed or never
     *  routed (provenance migration_2026_04). */
    unclassifiable: z.number().int().min(0),
    totalLeads: z.number().int().min(0),
    destinations: z.array(FsDestinationRowSchema),
    map: z.array(FsMapEntrySchema),
    /** Leads with no recorded state — the map's grand total stays honest. */
    mapNoState: z.number().int().min(0),
  })
  .strict();

export const FsHfPageSchema = z
  .object({
    daily: FsDailySeriesSchema,
    /** The dashed reached-Placed line — placements reaching Placed that day,
     *  a case Dropped later included (not Conversion; #1268). */
    convertedByDay: z.array(
      z.object({ date: z.string(), count: z.number().int().min(0) }).strict(),
    ),
    calendar: z.array(FsCalendarWeekSchema),
    statusCards: z.array(FsStatusCardSchema),
    statusCases: z.record(z.string(), z.array(FsCaseRowSchema)),
    /** Placements at Decision Assigned: created, not sent by LegalFlare, in no
     *  status card (the Under Review card notes them). #1268. */
    notSent: z.number().int().min(0),
    /** Send → decision, median days (the one decision clock). */
    decideClock: z.array(FsClockRowSchema),
    /** Decision → Placed (client-signed FSA back), median days. */
    fsaClock: z.array(FsClockRowSchema),
    map: z.array(FsMapEntrySchema),
    mapNoState: z.number().int().min(0),
  })
  .strict();

export const FsListOfRowSchema = z
  .object({
    firmId: z.string(),
    name: z.string(),
    /** Recency reads ARRIVAL, not the referral-event date — OF-1 asks whether
     *  work is still coming in (#680). For rows stamped by a bulk import,
     *  arrival is unknowable and the value falls back to the lead's real
     *  Creation Date, flagged below (ADR-0045: "estimates are marked, never
     *  hidden"). */
    lastLeadSent: z.string().nullable(),
    lastLeadSentEstimated: z.boolean(),
    sends7d: z.number().int().min(0),
    sends30d: z.number().int().min(0),
  })
  .strict();

export const FsListHfRowSchema = z
  .object({
    firmId: z.string(),
    name: z.string(),
    lastSentTo: z.string().nullable(),
    /** Decision Sent or Reviewing: sent, not yet accepted or declined. */
    underReviewNow: z.number().int().min(0),
    /** Decision Assigned: created, not sent by LegalFlare (#1268). */
    assignedNotSent: z.number().int().min(0),
    placedNow: z.number().int().min(0),
  })
  .strict();

export const FsDormantRowSchema = z
  .object({ firmId: z.string(), name: z.string() })
  .strict();

export const FsListsResponseSchema = z
  .object({
    /** Last finished day the atom series draw (yesterday, Pacific). */
    computedThrough: z.string(),
    of: z.array(FsListOfRowSchema),
    hf: z.array(FsListHfRowSchema),
    /** Registered firms with no activity at all in that capacity —
     *  out of the main list, findable, rejoin automatically on activity
     *  (R18: membership is activity-based). */
    dormantOf: z.array(FsDormantRowSchema),
    dormantHf: z.array(FsDormantRowSchema),
    /** This operator's hidden firms (display-only — hidden firms stay in
     *  every computation; server-side per login, R5/R6). */
    hiddenOf: z.array(z.string()),
    hiddenHf: z.array(z.string()),
  })
  .strict();

export const FsFirmPageResponseSchema = z
  .object({
    firmId: z.string(),
    name: z.string(),
    capacities: z.array(FsCapacitySchema).min(1),
    computedThrough: z.string(),
    of: FsOfPageSchema.nullable(),
    hf: FsHfPageSchema.nullable(),
  })
  .strict();

export const FsHiddenUpdateRequestSchema = z
  .object({
    capacity: FsCapacitySchema,
    firmId: z.string().min(1),
    hidden: z.boolean(),
  })
  .strict();

export const FsHiddenUpdateResponseSchema = z
  .object({
    ok: z.literal(true),
    hiddenOf: z.array(z.string()),
    hiddenHf: z.array(z.string()),
  })
  .strict();

export type FsDailyPoint = z.infer<typeof FsDailyPointSchema>;
export type FsDailySeries = z.infer<typeof FsDailySeriesSchema>;
export type FsCalendarDay = z.infer<typeof FsCalendarDaySchema>;
export type FsCalendarWeek = z.infer<typeof FsCalendarWeekSchema>;
export type FsStatusCard = z.infer<typeof FsStatusCardSchema>;
export type FsCaseRow = z.infer<typeof FsCaseRowSchema>;
export type FsConvCell = z.infer<typeof FsConvCellSchema>;
export type FsConversionRow = z.infer<typeof FsConversionRowSchema>;
export type FsClockRow = z.infer<typeof FsClockRowSchema>;
export type FsMapEntry = z.infer<typeof FsMapEntrySchema>;
export type FsDestinationRow = z.infer<typeof FsDestinationRowSchema>;
export type FsOfPage = z.infer<typeof FsOfPageSchema>;
export type FsHfPage = z.infer<typeof FsHfPageSchema>;
export type FsListOfRow = z.infer<typeof FsListOfRowSchema>;
export type FsListHfRow = z.infer<typeof FsListHfRowSchema>;
export type FsDormantRow = z.infer<typeof FsDormantRowSchema>;
export type FsListsResponse = z.infer<typeof FsListsResponseSchema>;
export type FsFirmPageResponse = z.infer<typeof FsFirmPageResponseSchema>;
export type FsHiddenUpdateRequest = z.infer<typeof FsHiddenUpdateRequestSchema>;
export type FsHiddenUpdateResponse = z.infer<
  typeof FsHiddenUpdateResponseSchema
>;
